<template>
  <div>
    <!-- 검색 영역 - PC/Mobile 공통 -->
    <vx-card class="search-section">
      <div class="flex items-center gap-4">
        <search-keyword label="" v-model="keyword" />
        <vs-button class="py-2 px-6" color="secondary" @click="loadPostList">검색</vs-button>
      </div>
    </vx-card>

    <!-- 헤더 영역 - PC/Mobile 공통 -->
    <div class="mt-4 flex justify-between items-center header-section">
      <div style="font-size: 21px; font-weight: 500">
        총 게시글 <span style="color: #7e72f2">{{ total }}</span
        >건
      </div>
    </div>

    <!-- PC 레이아웃 -->
    <vx-card class="mt-4 body-table desktop-layout">
      <!-- tool -->
      <div class="p-6 grid sm:flex items-center gap-4">
        <!-- perPage -->
        <vs-select v-model="perPage" @input="changePerPage">
          <vs-select-item v-for="pp in [10, 30, 50]" :key="`perPage_${pp}`" :value="pp" :text="`${pp}개씩 보기`" />
        </vs-select>

        <!-- sort -->
        <div class="flex gap-4 items-center" style="font-size: 17px; color: #555555">
          <span style="color: #1d1d1d; font-weight: 700">정렬기준</span>
          <a
            v-for="sortData in ['최신순', '가나다순']"
            :key="`sort_${sortData}`"
            :class="{ 'sort-active': sort === sortData }"
            href="javascript:;"
            @click="
              () => {
                sort = sortData;
                loadPostList();
              }
            "
            >{{ sortData }}</a
          >
        </div>
      </div>

      <!-- table -->
      <div class="overflow-x-auto">
        <table class="w-full">
          <colgroup>
            <col style="width: 96px; min-width: 96px" />
            <col style="width: 180px; min-width: 180px" />
            <col style="min-width: 300px" />
            <col style="width: 180px; min-width: 180px" />
            <col style="width: 180px; min-width: 180px" />
            <col style="width: 100px; min-width: 100px" />
            <col style="width: 120px; min-width: 120px" />
            <col style="width: 100px; min-width: 100px" />
          </colgroup>
          <tr>
            <th>No.</th>
            <th>교육대상</th>
            <th>강의명</th>
            <th>첨부파일</th>
            <th>교육이수일</th>
            <th>진행도</th>
            <th>시험결과</th>
            <th>시험응시</th>
          </tr>

          <tr v-for="(item, index) in list" :key="`pc_${index}`" @click="onClickDetail(item)">
            <td class="cursor-pointer">{{ (page - 1) * perPage + index + 1 }}</td>
            <td class="cursor-pointer">{{ item.body.교육대상 }}</td>
            <td class="cursor-pointer">{{ item.body.제목 }}</td>
            <td class="cursor-pointer">
              <div class="flex items-center justify-center">
                <vs-tooltip v-for="file in item.body.fileList" :key="`file_${file.path}`" :text="file.fileName || '{이름없음}'" placement="top">
                  <feather-icon icon="FileIcon" style="color: #7e72f2; cursor: pointer; width: 20px" @click.stop="onClickFile(file)" />
                </vs-tooltip>
              </div>
            </td>
            <td class="cursor-pointer">{{ item.body.교육이수일 }}</td>
            <td class="cursor-pointer">{{ Math.min(parseInt((item.body.watchedSeconds / item.body.duration) * 10000) / 100, 100) }}%</td>
            <td class="cursor-pointer">
              <span v-if="item.body.시험결과 === '합격'" class="btn success">합격</span>
              <span v-else-if="item.body.시험결과 === '불합격'" class="btn danger">불합격</span>
              <span v-else class="btn darken">미응시</span>
            </td>
            <td :class="[item.body.watchedSeconds >= item.body.watchedSecondsForPass ? 'cursor-pointer' : '']" @click.stop="onClickCheckList(item)">
              <feather-icon v-if="item.body.watchedSeconds >= item.body.watchedSecondsForPass" icon="ExternalLinkIcon" style="color: #7e72f2; width: 20px" />
              <vs-tooltip v-else :text="`진행도 ${item.body.validWatchProgress}% 이상 필요`" placement="top">
                <feather-icon icon="ExternalLinkIcon" style="color: #cdcdcd; width: 20px" @click.stop />
              </vs-tooltip>
            </td>
          </tr>
        </table>
      </div>

      <!-- padding -->
      <div class="py-6 vs-con-table">
        <div class="con-pagination-table vs-table--pagination">
          <k-pagination :total="lastPage || 1" :value="page" @input="changePage"></k-pagination>
        </div>
      </div>
    </vx-card>

    <!-- 모바일 레이아웃 -->
    <div class="mobile-layout mt-4">
      <!-- 정렬 및 필터 -->
      <vx-card class="mobile-filter">
        <div class="flex items-center justify-between gap-4 p-4">
          <!-- 정렬 기준 -->
          <div class="flex gap-3 items-center">
            <span class="text-sm font-semibold text-gray-700">정렬</span>
            <select v-model="sort" @change="loadPostList" class="mobile-select">
              <option value="최신순">최신순</option>
              <option value="가나다순">가나다순</option>
            </select>
          </div>
        </div>
      </vx-card>

      <!-- 카드 컨테이너 -->
      <div v-if="list.length > 0" class="mobile-cards-container">
        <div class="mobile-cards-wrapper" ref="cardsWrapper" @touchstart="handleTouchStart" @touchmove="handleTouchMove" @touchend="handleTouchEnd" :style="{ transform: `translateX(-${currentCardIndex * 100}%)` }">
          <div v-for="(item, index) in displayedList" :key="`mobile_${index}`" class="mobile-card" @click="onClickDetail(item)">
            <div class="card-header">
              <div class="card-number">{{ (currentPage - 1) * mobileCardsPerPage + index + 1 }}</div>
              <div class="card-target">{{ item.body.교육대상 }}</div>
            </div>

            <div class="card-title">{{ item.body.제목 }}</div>

            <!-- 교육 정보 섹션 -->
            <div class="card-progress">
              <div class="progress-row">
                <feather-icon icon="BarChart3Icon" size="16" class="info-icon" />
                <span class="info-label">진행도:</span>
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: `${Math.min(parseInt((item.body.watchedSeconds / item.body.duration) * 10000) / 100, 100)}%` }"></div>
                  <span class="progress-text">{{ Math.min(parseInt((item.body.watchedSeconds / item.body.duration) * 10000) / 100, 100) }}%</span>
                </div>
              </div>
              <div class="result-row">
                <feather-icon icon="AwardIcon" size="16" class="info-icon" />
                <span class="info-label">시험결과:</span>
                <span v-if="item.body.시험결과 === '합격'" class="result-badge success">합격</span>
                <span v-else-if="item.body.시험결과 === '불합격'" class="result-badge danger">불합격</span>
                <span v-else class="result-badge default">미응시</span>
              </div>
              <div class="info-row">
                <feather-icon icon="CalendarIcon" size="16" class="info-icon" />
                <span class="info-label">교육이수일:</span>
                <span class="info-value">{{ item.body.교육이수일 }}</span>
              </div>
            </div>

            <div v-if="item.body.fileList && item.body.fileList.length > 0" class="card-files">
              <div class="files-header">
                <feather-icon icon="PaperclipIcon" size="16" />
                <span class="files-label">첨부파일</span>
                <span class="files-count">({{ item.body.fileList.length }}개)</span>
              </div>
              <div class="files-list">
                <div v-for="file in item.body.fileList.slice(0, 3)" :key="`mobile_file_${file.path}`" class="file-item" @click.stop="onClickFile(file)">
                  <feather-icon icon="FileIcon" size="14" />
                  <span>{{ file.fileName || "{이름없음}" }}</span>
                </div>
                <div v-if="item.body.fileList.length > 3" class="file-more">+{{ item.body.fileList.length - 3 }}개 더보기</div>
              </div>
            </div>

            <!-- 시험응시 버튼 -->
            <div class="exam-section">
              <div v-if="item.body.시험결과 === '합격'" class="exam-button-container">
                <vs-button class="exam-button passed" color="success" @click.stop="onClickCheckList(item)">
                  <feather-icon icon="CheckCircleIcon" size="16" />
                  시험 결과 보기
                </vs-button>
              </div>
              <div v-else-if="item.body.watchedSeconds >= item.body.watchedSecondsForPass" class="exam-button-container">
                <vs-button class="exam-button available" color="primary" @click.stop="onClickCheckList(item)">
                  <feather-icon icon="PlayCircleIcon" size="16" />
                  시험 응시하기
                </vs-button>
              </div>
              <div v-else class="exam-button-container disabled">
                <vs-button class="exam-button disabled" disabled color="secondary" type="border">
                  <feather-icon icon="LockIcon" size="16" />
                  시험 응시 불가
                </vs-button>
                <div class="exam-notice">
                  <feather-icon icon="InfoIcon" size="14" />
                  <span>진행도 {{ item.body.validWatchProgress }}% 이상 필요</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 카드 인디케이터 -->
        <div class="card-indicators">
          <div v-for="(item, index) in displayedList" :key="`indicator_${index}`" :class="['indicator', { active: index === currentCardIndex }]" @click="goToCard(index)"></div>
        </div>

        <!-- 더보기 버튼 -->
        <div v-if="hasMoreCards" class="load-more-container">
          <vs-button class="load-more-btn" color="secondary" type="border" @click="loadMoreCards" :loading="loadingMore"> 더보기 ({{ remainingCards }}개) </vs-button>
        </div>
      </div>

      <!-- 빈 목록 -->
      <vx-card v-else class="empty-state">
        <div class="empty-content">
          <feather-icon icon="BookOpenIcon" size="48" class="empty-icon" />
          <h3>수강한 교육이 없습니다</h3>
          <p>교육을 수강한 후 시험에 응시해보세요.</p>
        </div>
      </vx-card>
    </div>

    <vs-popup id="detailPopup" :active.sync="openPostDetail" title="안전보건 교육 수강" @close="closePopupDetail" :style="{ zIndex: 53000 }">
      <detail ref="DetailPage" :data="selectPost" :permission="permission" @edit="onClickEdit" @delete="onClickDelete" />
    </vs-popup>

    <vs-popup id="questionsPopup" :active.sync="openQuestions" title="시험응시" @close="closeQuestions" :style="{ zIndex: 53000 }">
      <questions ref="QuestionsPage" :data="selectPost" @close="closeQuestions" @result="onResultQuestions" />
    </vs-popup>

    <vs-popup id="resultPopup" :active.sync="openResult" title="시험응시 결과" @close="closeResult" :style="{ zIndex: 53000 }">
      <result :result="result" @close="closeResult" @retry="onRetryQuestions" @review="reviewResult" />
    </vs-popup>

    <vs-popup id="reviewPopup" :active.sync="openReview" title="시험응시 선택" @close="closeReview" :style="{ zIndex: 53000 }">
      <review v-if="openReview" :data="selectPost" @close="closeReview" @re-questions="onReQuestions"/>
    </vs-popup>
  </div>
</template>

<script>
import SearchKeyword from "@/views/list/components/SearchKeyword.vue";
import KPagination from "@/components/kPagination/kPagination.vue";
import Detail from "@/views/boards/single-page/1064/Detail.vue";
import * as DataUtil from "@/util/DataUtil";
import * as UiUtil from "@/util/UiUtil";
import Questions from "@/views/boards/single-page/1065/Questions.vue";
import Result from "@/views/boards/single-page/1065/Result.vue";
import Review from "@/views/boards/single-page/1065/Review.vue";

export default {
  components: {
    Review,
    Result,
    Questions,
    Detail,
    KPagination,
    SearchKeyword,
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    boardId() {
      return this.$route.params.boardId;
    },
    myInfo() {
      return this.$store.state.auth.userInfo;
    },
    displayedList() {
      return this.list.slice(0, this.currentPage * this.mobileCardsPerPage);
    },
    hasMoreCards() {
      return this.displayedList.length < this.total;
    },
    remainingCards() {
      return this.total - this.displayedList.length;
    },
  },
  data() {
    return {
      permission: { c: 0, r: 0, u: 0, d: 0 }, // 게시판 권한
      page: 1, // 현재 페이지 (PC용)
      perPage: 10, // 페이지당 게시글 수
      lastPage: 1, // 마지막 페이지
      total: 0, // 총 게시글 수
      keyword: "", // 검색 키워드
      sort: "최신순",
      list: [], // 게시글 목록

      selectPost: null, // 선택된 게시글
      openPostDetail: false, // 상세보기 열림 여부
      openQuestions: false, // 시험응시 열림 여부
      result: null, // 시험결과
      openResult: false, // 시험결과 열림 여부
      openReview: false, // 시험응시 선택 열림 여부

      // 모바일 전용
      currentCardIndex: 0, // 현재 카드 인덱스
      currentPage: 1, // 모바일 페이지
      mobileCardsPerPage: 5, // 모바일에서 한 번에 로드할 카드 수
      loadingMore: false, // 더보기 로딩 상태

      // 터치 관련
      touchStartX: 0,
      touchEndX: 0,
      isDragging: false,
    };
  },
  async mounted() {
    await this.loadPermission();
    await this.loadPostList();
  },
  methods: {
    async loadPermission() {
      try {
        UiUtil.loading(true)
        const boardId = 1064; // 상세 팝업에서 수정/삭제 버튼, 수강인원 항목등의 권한에 따라 다르게 처리하기 위해서 교육수강(1064) 권한을 가져온다.
        if (Number.isInteger(parseInt(boardId))) {
          let boards = this.$store.state.member.memberInfo.boards;
          if (!boards) {
            await this.$store.dispatch("member/LOAD_MEMBER_DETAIL");
            boards = this.$store.state.member.memberInfo.boards;
          }

          let board = boards.find((b) => parseInt(b.id) === parseInt(boardId));
          if (!!board && !!board.pivot) {
            if (board.pivot.project_id) {
              // 프로젝트에 맞는 권한이 있는지 다시 조사한다
              board = boards.find((b) => parseInt(b.pivot.board_id) === parseInt(boardId) && parseInt(b.pivot.project_id) === parseInt(this.projectId));
              this.permission = { ...board.pivot };
            } else {
              this.permission = { ...board.pivot };
            }
          }
        }
      } catch (error) {

      } finally {
        UiUtil.loading(false)
      }
    },

    async loadPostList() {
      try {
        UiUtil.loading(true)
        const projectId = this.projectId;
        const boardId = this.boardId;
        const page = this.page;
        const perPage = this.perPage;
        const sort = this.sort;
        const keyword = this.keyword.trim().toLowerCase();
        const conditions = [];
        const orders = [];

        conditions.push({ key: "user_id", operator: "=", value: this.myInfo.id });
        if (keyword) {
          conditions.push({ key: "body.교육대상", operator: "LIKE", value: `%${keyword}%` });
          conditions.push({ key: "body.제목", operator: "LIKE", value: `%${keyword}%`, boolean: "OR" });
        }

        if (sort === "최신순") {
          orders.push({ target: "id", direction: "desc" });
        } else if (sort === "가나다순") {
          orders.push({ target: "$body.제목", direction: "asc" });
        }

        const listInfo = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId,
          boardId,
          page,
          perPage,
          conditions,
          orders,
        });

        this.lastPage = listInfo.last_page;
        this.total = listInfo.total;
        const list = listInfo.data.map((item) => {
          const content = JSON.parse(item.post_content);
          return {
            ...item,
            header: content.header,
            body: content.body,
          };
        });

        if (list.length > 0) {
          for (const item of list) {
            const content = JSON.parse(item.post_content);
            await this.$store
              .dispatch("board/LOAD_POST", {
                projectId,
                boardId: 1064, // 수강내역을 가져오기 위해서 1065 게시판에서 검색
                postId: content.body.videoPostId,
              })
              .then((res) => {
                if (res) {
                  const body1064 = JSON.parse(res.post_content).body;
                  item.post_content = JSON.stringify(content);
                  item.body = {
                    ...body1064,
                    ...content.body,
                  };
                  item.latestQuestions = body1064.questions;
                }
              });
          }
        }
        this.list = list;
        this.currentCardIndex = 0; // 카드 인덱스 초기화
      } catch (error) {

      } finally {
        UiUtil.loading(false)
      }
    },
    formatDuration(seconds) {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60)
        .toString()
        .padStart(2, "0");
      return `${m}:${s}`;
    },
    changePage(page) {
      this.page = page;
      this.loadPostList();
    },
    changePerPage() {
      this.page = 1;
      this.loadPostList();
    },

    async loadMoreCards() {
      if (this.loadingMore || !this.hasMoreCards) return;

      this.loadingMore = true;
      this.currentPage++;

      // 추가 데이터 로드
      const projectId = this.projectId;
      const boardId = this.boardId;
      const page = this.currentPage;
      const perPage = this.mobileCardsPerPage;
      const sort = this.sort;
      const keyword = this.keyword.trim().toLowerCase();
      const conditions = [];
      const orders = [];

      conditions.push({ key: "user_id", operator: "=", value: this.myInfo.id });
      if (keyword) {
        conditions.push({ key: "body.교육대상", operator: "LIKE", value: `%${keyword}%` });
        conditions.push({ key: "body.제목", operator: "LIKE", value: `%${keyword}%`, boolean: "OR" });
      }

      if (sort === "최신순") {
        orders.push({ target: "id", direction: "desc" });
      } else if (sort === "가나다순") {
        orders.push({ target: "$body.제목", direction: "asc" });
      }

      try {
        const listInfo = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId,
          boardId,
          page,
          perPage,
          conditions,
          orders,
        });

        const newList = listInfo.data.map((item) => {
          const content = JSON.parse(item.post_content);
          return {
            ...item,
            header: content.header,
            body: content.body,
          };
        });

        // 추가 데이터 처리
        if (newList.length > 0) {
          for (const item of newList) {
            const content = JSON.parse(item.post_content);
            await this.$store
              .dispatch("board/LOAD_POST", {
                projectId,
                boardId: 1064,
                postId: content.body.videoPostId,
              })
              .then((res) => {
                if (res) {
                  const body1064 = JSON.parse(res.post_content).body;
                  item.post_content = JSON.stringify(content);
                  item.body = {
                    ...body1064,
                    ...content.body,
                  };
                  item.latestQuestions = body1064.questions;
                }
              });
          }
        }

        this.list = [...this.list, ...newList];
      } catch (error) {
        console.error("추가 데이터 로드 실패:", error);
      } finally {
        this.loadingMore = false;
      }
    },

    // 터치 이벤트 핸들러
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.isDragging = true;
    },
    handleTouchMove(e) {
      if (!this.isDragging) return;
      this.touchEndX = e.touches[0].clientX;
    },
    handleTouchEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;

      const deltaX = this.touchStartX - this.touchEndX;
      const threshold = 50; // 최소 스와이프 거리

      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0 && this.currentCardIndex < this.displayedList.length - 1) {
          // 왼쪽으로 스와이프 (다음 카드)
          this.currentCardIndex++;
        } else if (deltaX < 0 && this.currentCardIndex > 0) {
          // 오른쪽으로 스와이프 (이전 카드)
          this.currentCardIndex--;
        }
      }
    },
    goToCard(index) {
      this.currentCardIndex = index;
    },
    async onClickDetail(data) {
      // 수강인원 정보를 가져온다.
      UiUtil.loading(true);
      // 수강내역에 등록된 문서인지 검색한다.
      await this.$store
        .dispatch("board/LOAD_POST_LIST", {
          projectId: this.projectId,
          boardId: 1065,
          conditions: [{ key: "body.videoPostId", operator: "=", value: data.body.videoPostId }],
          page: 1,
          perPage: 1,
        })
        .then((res) => {
          if (res) {
            data.수강인원 = res.total;
          }
        });
      UiUtil.loading(false);

      this.selectPost = data;
      this.selectPost.수강내역 = JSON.parse(JSON.stringify(data)); // Detail.vue 를 1064, 1065에서 공통적으로 사용하기 위해 수강내역에 다시 저장 (data.수강내역)
      this.openPostDetail = true;

      this.$nextTick(() => {
        if (data.body.videoId) {
          this.$refs.DetailPage.start();
        }
      });
    },
    onClickFile(file) {
      if (file && file.fileName && file.path) {
        DataUtil.downloadFile(file.fileName, file.path);
      } else {
        alert("파일 정보가 올바르지 않습니다.");
      }
    },
    onClickEdit(data) {
      const postId = data.수강내역.body.videoPostId;
      this.$refs.DetailPage.pause();
      this.openPostDetail = false;

      const query = this.$route.query || {};

      this.$nextTick(() => {
        this.$router.push({
          name: "write|update|tab",
          params: {
            pageType: "write",
            proj_id: this.projectId,
            boardId: 1064,
            postId,
          },
          query: {
            ...query,
            _t_back: "1064/page",
          },
        });
      });
    },
    onClickDelete(data) {
      // ** 1064 boardId 를 가져온다.
      // ** 수강내역을 삭제하는 기능은 없다.
      const deletePostId = data.수강내역.body.videoPostId;
      this.$vs.dialog({
        type: "confirm",
        title: "교육 수강 삭제",
        text: "정말로 해당 교육 수강을 삭제하시겠습니까?",
        acceptText: "예",
        cancelText: "아니오",
        accept: async () => {
          // 관련 수강내역 모두 삭제
          const list = await this.$store.dispatch("board/LOAD_POST_LIST", {
            projectId: this.projectId,
            boardId: 1065,
            conditions: [{ key: "body.videoPostId", operator: "=", value: deletePostId }],
          });
          if (list.data.length > 0) {
            for (const item of list.data) {
              await this.$store
                .dispatch("board/DELETE_POST", {
                  projectId: this.projectId,
                  boardId: 1065,
                  postId: item.id,
                })
                .catch((err) => {
                  console.error("수강내역 삭제 실패", err);
                });
            }
          }

          // 교육 수강 삭제
          await this.$store.dispatch("board/DELETE_POST", {
            projectId: this.projectId,
            boardId: 1064,
            postId: deletePostId,
          });
          this.$refs.DetailPage.pause();
          this.openPostDetail = false;
          await this.$nextTick();
          setTimeout(() => {
            this.loadPostList();
          }, 300);
        },
      });
    },
    async closePopupDetail() {
      await this.$refs.DetailPage.end();
      // 1064 페이지에서는 상세페이지로 이동할 때, 1065를 매번 검색하여 변경된 데이터를 가져오기 때문에,
      // 리스트를 다시 불러올 필요가 없지만, 1065페이지에서는 그러한 과정이 없기 때문에
      // 팝업이 닫힐 때 리스트를 다시 불러온다.
      await this.loadPostList();
    },
    closeQuestions() {
      this.openQuestions = false;
      this.$refs.QuestionsPage.reset();
    },
    async closeResult() {
      this.openResult = false;
      await this.loadPostList();
    },
    closeReview() {
      this.openReview = false;
    },
    onReQuestions() {
      this.openReview = false;
      if (this.selectPost.latestQuestions) {
        this.selectPost.body.questions = this.selectPost.latestQuestions;
      }
      this.openQuestions = true;
      this.$nextTick(() => {
        this.$refs.QuestionsPage.load();
      });
    },
    async reviewResult() {
      // TODO: selectPost Update 필요
      this.openResult = false;

      this.selectPost = JSON.parse(JSON.stringify(this.selectPost));
      this.openReview = true;
      this.$nextTick(() => {
        this.loadPostList();
      });
    },
    onRetryQuestions() {
      this.openResult = false;
      if (this.selectPost.latestQuestions) {
        this.selectPost.body.questions = this.selectPost.latestQuestions;
      }
      this.openQuestions = true;
      this.$nextTick(() => {
        this.$refs.QuestionsPage.load();
      });
    },
    onClickCheckList(item) {
      if (item.body.시험결과 === "합격") {
        this.selectPost = JSON.parse(JSON.stringify(item));
        this.selectPost.latestQuestions = item.latestQuestions;
        this.openReview = true;
        return;
      }

      if (item.body.watchedSeconds >= item.body.watchedSecondsForPass) {
        this.selectPost = JSON.parse(JSON.stringify(item));
        if (item.latestQuestions) {
          this.selectPost.body.questions = item.latestQuestions;
          this.selectPost.latestQuestions = item.latestQuestions;
        }
        this.openQuestions = true;
        this.$nextTick(() => {
          this.$refs.QuestionsPage.load();
        });
      }
    },

    async onResultQuestions(result, questions, submit) {
      this.closeQuestions();
      this.result = {
        name: `${this.selectPost.author.name}님(${this.selectPost.author.department}, ${this.selectPost.author.rank})`,
        title: this.selectPost.body.제목,
        passed: result.passed,
        successCount: result.success,
        failCount: result.fail,
        교육이수일: result.교육이수일 || "",
        교육만료일: result.교육만료일 || "",
      };

      // 시험결과에서 post_content 의  body.submit 값을 사용하므로, 업데이트한다.
      const content = JSON.parse(this.selectPost.post_content);
      content.body.questions = questions;
      content.body.submit = submit;
      this.$set(this.selectPost, "post_content", JSON.stringify(content));
      this.$set(this.selectPost.body, "questions", questions);
      this.openResult = true;

      await this.$nextTick();
      await this.loadPostList();
    },
  },
};
</script>

<style scoped lang="scss">
// 검색 영역 모바일 여백
.search-section {
  @media (max-width: 768px) {
    width: calc(100% - 32px);
    margin: 0 16px; // 모바일에서 검색 영역 좌우 여백 추가
  }
}

// 헤더 영역 모바일 여백
.header-section {
  @media (max-width: 768px) {
    padding: 0 16px; // 모바일에서 헤더 영역 좌우 여백 추가
  }
}

// PC 레이아웃
.desktop-layout {
  display: block;

  @media (max-width: 768px) {
    display: none !important;
    margin: 0 16px; // 혹시 표시될 경우를 위한 여백
  }
}

// 모바일 레이아웃
.mobile-layout {
  display: none;

  @media (max-width: 768px) {
    display: block;
    padding: 0 16px; // 페이지 양옆 여백 추가
  }
}

// 모바일 필터
.mobile-filter {
  margin: 0 0 16px 0; // 하단 여백만 추가

  .mobile-select {
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    font-size: 14px;
    min-width: 100px;
  }
}

// 모바일 카드 컨테이너
.mobile-cards-container {
  position: relative;
  overflow: hidden;
  margin-top: 16px;
}

.mobile-cards-wrapper {
  display: flex;
  transition: transform 0.3s ease;
  width: calc(100% * var(--card-count, 1));
}

.mobile-card {
  flex: 0 0 calc(100%);
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin: 0;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:active {
    transform: scale(0.98);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  .card-number {
    background: #7e72f2;
    color: white;
    font-size: 12px;
    font-weight: bold;
    padding: 4px 8px;
    border-radius: 12px;
    min-width: 32px;
    text-align: center;
  }

  .card-target {
    background: #f0f0f0;
    color: #666;
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 8px;
    font-weight: 500;
  }
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1d;
  margin-bottom: 16px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

// 진행도와 시험결과를 포함한 정보 섹션
.card-progress {
  margin-bottom: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;

  .progress-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;

    .info-icon {
      color: #7e72f2;
      flex-shrink: 0;
      width: 16px;
    }

    .info-label {
      color: #666;
      font-weight: 500;
      flex-shrink: 0;
      min-width: 80px;
      font-size: 14px;
    }

    .progress-bar {
      position: relative;
      flex: 1;
      height: 22px;
      background: #e9ecef;
      border-radius: 11px;
      overflow: hidden;

      .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #7e72f2, #9c88ff);
        border-radius: 11px;
        transition: width 0.3s ease;
      }

      .progress-text {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 11px;
        font-weight: bold;
        color: #333;
        text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
      }
    }
  }

  .result-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;

    .info-icon {
      color: #7e72f2;
      flex-shrink: 0;
      width: 16px;
    }

    .info-label {
      color: #666;
      font-weight: 500;
      flex-shrink: 0;
      min-width: 80px;
      font-size: 14px;
    }

    .result-badge {
      padding: 6px 14px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;

      &.success {
        background: #d4edda;
        color: #155724;
      }

      &.danger {
        background: #f8d7da;
        color: #721c24;
      }

      &.default {
        background: #e2e3e5;
        color: #383d41;
      }
    }
  }

  .info-row {
    display: flex;
    align-items: center;
    gap: 12px;

    .info-icon {
      color: #7e72f2;
      flex-shrink: 0;
      width: 16px;
    }

    .info-label {
      color: #666;
      font-weight: 500;
      flex-shrink: 0;
      min-width: 80px;
      font-size: 14px;
    }

    .info-value {
      color: #333;
      font-weight: 400;
      flex: 1;
    }
  }
}

.card-files {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
  margin-bottom: 16px;

  .files-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    margin-bottom: 8px;

    .files-label {
      color: #666;
      font-weight: 500;
    }

    .files-count {
      color: #999;
      font-weight: 400;
      font-size: 13px;
    }
  }

  .files-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .file-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #7e72f2;
    padding: 6px 8px;
    background: #f8f7ff;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:active {
      background: #ede8ff;
    }

    span {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .file-more {
    font-size: 12px;
    color: #999;
    text-align: center;
    padding: 4px;
  }
}

// 시험응시 섹션 (가장 중요한 부분)
.exam-section {
  border-top: 2px solid #f0f0f0;
  padding-top: 16px;
  margin-top: 16px;

  .exam-button-container {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &.disabled {
      opacity: 0.7;
    }

    .exam-button {
      width: 100%;
      min-height: 48px;
      font-size: 16px;
      font-weight: 600;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      transition: all 0.2s ease;

      &.passed {
        background: linear-gradient(135deg, #28c76f, #20bf6b);
        box-shadow: 0 4px 12px rgba(40, 199, 111, 0.3);

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(40, 199, 111, 0.4);
        }
      }

      &.available {
        background: linear-gradient(135deg, #7e72f2, #9c88ff);
        box-shadow: 0 4px 12px rgba(126, 114, 242, 0.3);
        animation: pulse-glow 2s infinite;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(126, 114, 242, 0.4);
        }
      }

      &.disabled {
        background: #f8f9fa;
        color: #6c757d;
        box-shadow: none;
        cursor: not-allowed;
      }
    }

    .exam-notice {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      background: #fff3cd;
      border: 1px solid #ffeaa7;
      border-radius: 8px;
      font-size: 13px;
      color: #856404;

      feather-icon {
        color: #f39c12;
        flex-shrink: 0;
      }

      span {
        font-weight: 500;
      }
    }
  }
}

@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 4px 12px rgba(126, 114, 242, 0.3);
  }
  50% {
    box-shadow: 0 4px 20px rgba(126, 114, 242, 0.5);
  }
}

// 카드 인디케이터
.card-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 20px 0;
  padding: 0;

  .indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d0d0d0;
    cursor: pointer;
    transition: all 0.2s ease;

    &.active {
      background: #7e72f2;
      transform: scale(1.2);
    }
  }
}

// 더보기 버튼
.load-more-container {
  margin: 24px 0;
  text-align: center;

  .load-more-btn {
    min-height: 44px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 500;
  }
}

// 빈 상태
.empty-state {
  margin: 16px 0 40px 0;

  .empty-content {
    text-align: center;
    padding: 40px 20px;

    .empty-icon {
      color: #d0d0d0;
      margin-bottom: 16px;
    }

    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #666;
      margin-bottom: 8px;
    }

    p {
      font-size: 14px;
      color: #999;
    }
  }
}

// 기존 PC 스타일 유지
.sort-active {
  text-decoration: underline;
  color: #1d1d1d;
}

.body-table::v-deep {
  .vx-card__collapsible-content .vx-card__body {
    padding: 0;
  }
}

.body-table {
  table {
    border-collapse: collapse;
    border-top: 1px solid #cdcdcd;

    th {
      background-color: #f9f9f9;
      text-align: center;
      padding: 1rem;
      font-weight: 500;
      border-bottom: 1px solid #cdcdcd;
    }

    td {
      text-align: center;
      padding: 1rem;
      font-weight: 400;
      color: #3c3c3c;
      border-bottom: 1px solid #cdcdcd;
    }
  }
}

.btn {
  padding: 4px 7px;
  border-radius: 4px;
  color: #fff;

  &.success {
    background-color: #28c76f;
  }
  &.danger {
    background-color: #ea5455;
  }
  &.darken {
    background-color: #696d71;
  }
}

#detailPopup::v-deep {
  .vs-popup {
    width: 90%;
    max-width: 1200px;
  }
}

#resultPopup::v-deep,
#questionsPopup::v-deep,
#reviewPopup::v-deep {
  .vs-popup {
    width: 90%;
    max-width: 740px;
  }
}

// 인쇄 모드에서는 PC 레이아웃 강제 표시
@media print {
  .desktop-layout {
    display: block !important;
  }

  .mobile-layout {
    display: none !important;
  }
}
</style>
